import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { elementToPdf } from "@/lib/pdf";
import { fmtDate } from "@/lib/format";
import { getPrintBrand } from "@/lib/print";

function ReportDoc({ title, subtitle, columns, rows, totals = [], brand }) {
  const { view, t, lang, dir } = useApp();
  const logo = getPrintBrand()?.logoUrl;
  return (
    <div dir={dir} className="bg-white text-[#12384A] text-[13px]" style={{ width: 794, minHeight: 1123 }}>
      <div className="bg-[#0B2A5B] text-white px-10 py-6 flex justify-between items-end">
        <div className="flex items-center gap-4">
          {logo && <img src={logo} alt="" data-testid="pdf-report-logo" style={{ width: 64, height: 64, objectFit: "contain", background: "#fff", borderRadius: 12, padding: 4 }} />}
          <div>
            <div className="text-xl font-bold">{brand || view.shop?.name}</div>
            <div className="text-white/70 text-xs">{[view.shop?.address, view.shop?.phone].filter(Boolean).join(" · ")}</div>
          </div>
        </div>
        <div className="text-end text-xs text-white/80">{t("app_name")}<br />{t("generated_at")}: {fmtDate(new Date().toISOString(), lang)}</div>
      </div>
      <div className="px-10 py-6">
        <h1 className="text-2xl font-bold">{title}</h1>
        <div className="h-0.5 w-16 bg-[#F0444B] my-2" />
        {subtitle && <p className="text-[#5b6b75] mb-4">{subtitle}</p>}
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#0B2A5B] text-white">
              {columns.map((c) => <th key={c.key} className={`p-2 font-semibold ${c.num ? "text-end" : "text-start"}`}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className={i % 2 ? "bg-[#EEF5FB]" : ""}>
                {columns.map((c) => <td key={c.key} className={`p-2 border-b border-[#dbe6ef] ${c.num ? "text-end" : ""}`}>{r[c.key]}</td>)}
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={columns.length} className="p-4 text-center text-[#5b6b75]">{t("no_history")}</td></tr>}
          </tbody>
        </table>
        {totals.length > 0 && (
          <div className="mt-6 ms-auto w-72 border border-[#dbe6ef] rounded-lg overflow-hidden">
            {totals.map(([k, v], i) => (
              <div key={k} className={`flex justify-between px-3 py-2 ${i === totals.length - 1 ? "bg-[#0B2A5B] text-white font-bold" : "border-b border-[#dbe6ef]"}`}><span>{k}</span><span>{v}</span></div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function usePdfReport() {
  const { t } = useApp();
  const [doc, setDoc] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    if (!doc) return undefined;
    const id = setTimeout(async () => {
      try { await elementToPdf(ref.current, doc.filename); } catch (e) { toast.error(String(e?.message || e)); }
      setDoc(null);
    }, 120);
    return () => clearTimeout(id);
  }, [doc]);
  const node = doc ? createPortal(
    <div style={{ position: "fixed", left: -10000, top: 0 }} aria-hidden="true"><div ref={ref}><ReportDoc {...doc} /></div></div>,
    document.body,
  ) : null;
  const exportReport = (d) => { toast.message(t("generating")); setDoc(d); };
  return { exportReport, node, busy: !!doc };
}
